import React from "react";
import { Eye, RotateCw, Search } from "lucide-react";
import { Button } from "../ui/Button";

export interface ZReportItem {
  id: string;
  date: string;
  dateObj?: Date;
  noOfSales: number;
  totalNetSale: string;
  totalProductSaleNet: string;
  totalServicesSaleNet: string;
  totalTendering: string;
}

interface ZReportTableProps {
  reports: ZReportItem[];
  isLoading: boolean;
  indexOfFirstItem: number;
  onView: (report: ZReportItem) => void;
  onSync: (report: ZReportItem) => void;
}

export const ZReportTable: React.FC<ZReportTableProps> = ({
  reports,
  isLoading,
  indexOfFirstItem,
  onView,
  onSync,
}) => {
  return (
    <div className="flex-1 lg:overflow-auto relative custom-scrollbar">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold whitespace-nowrap">#</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Date</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Sales</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Total Net Sale</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Product Sale Net</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Services Sale Net</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Tendering</th>
            <th className="col-span-1 text-right font-bold whitespace-nowrap">Action</th>
          </tr>
        </thead>
        <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0 p-4 lg:p-0">
          {isLoading ? (
            <tr className="h-64 w-full flex items-center justify-center lg:table-row">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : reports.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
              <td colSpan={12} className="w-full h-full p-0">
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <Search className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-lg font-medium text-gray-500">
                    No reports found
                  </p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              {reports.map((report, idx) => (
                <tr
                  key={report.id}
                  className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 items-stretch lg:items-center px-5 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full"
                >
                  <td className="lg:col-span-1 text-gray-400 font-bold lg:font-medium flex justify-between items-center lg:block">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Record #</span>
                    <span>{indexOfFirstItem + idx + 1}</span>
                  </td>

                  <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                    <span className="truncate">{report.date}</span>
                  </td>

                  <td className="lg:col-span-1 font-medium text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Sales</span>
                    <span className="font-bold lg:font-normal">{report.noOfSales}</span>
                  </td>

                  <td className="lg:col-span-2 font-bold text-[#885ABB] lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Net Sale</span>
                    <span className="text-base lg:text-sm">{report.totalNetSale}</span>
                  </td>

                  <td className="lg:col-span-2 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Product Sale</span>
                    <span className="truncate">{report.totalProductSaleNet}</span>
                  </td>

                  <td className="lg:col-span-2 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Service Sale</span>
                    <span className="truncate">{report.totalServicesSaleNet}</span>
                  </td>

                  <td className="lg:col-span-1 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Tendering</span>
                    <span className="truncate">{report.totalTendering}</span>
                  </td>

                  <td className="lg:col-span-1 border-t border-gray-50 lg:border-0 pt-4 lg:pt-0">
                    <div className="flex justify-end lg:justify-end gap-3 w-full">
                      <Button
                        size="sm"
                        className="flex-1 lg:flex-none h-11 lg:h-9 bg-[#F3E8FF] hover:bg-[#E9D5FF] text-[#885ABB] rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm border border-transparent flex items-center justify-center gap-2"
                        onClick={() => onView(report)}
                        title="View Report"
                      >
                        <Eye size={18} className="lg:w-4 lg:h-4" />
                        <span className="lg:hidden">View Report</span>
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 lg:flex-none h-11 lg:h-9 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm border border-transparent flex items-center justify-center gap-2"
                        onClick={() => onSync(report)}
                        title="Sync/Refresh"
                      >
                        <RotateCw size={18} className="lg:w-4 lg:h-4" />
                        <span className="lg:hidden">Sync</span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
